/**
 * SEO утилиттері
 * Беттің мета тегтерін басқаруға арналған функциялар
 */

/**
 * Беттің тақырыбын орнату
 * @param {string} title - Беттің атауы
 * @param {string} siteName - Сайттың атауы (қосымша)
 */
export function setTitle(title, siteName = 'Agroland.kz') {
  if (title && siteName) {
    document.title = `${title} — ${siteName}`;
  } else if (title) {
    document.title = title;
  } else {
    document.title = siteName;
  }
}

/**
 * Meta тегті орнату немесе жаңарту
 * @param {string} name - Meta тегтің атауы
 * @param {string} content - Meta тегтің мәні
 * @param {string} type - 'name' немесе 'property'
 */
function setMetaTag(name, content, type = 'name') {
  let meta = document.querySelector(`meta[${type}="${name}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(type, name);
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
}

/**
 * Link тегті орнату (canonical, alternate, etc.)
 * @param {string} rel - Rel атрибуты
 * @param {string} href - Href атрибуты
 */
function setLinkTag(rel, href) {
  let link = document.querySelector(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

/**
 * Structured Data (JSON-LD) орнату
 * @param {Object} data - Schema.org деректері
 */
export function setStructuredData(data) {
  let script = document.querySelector('script[type="application/ld+json"]');

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

/**
 * Барлық SEO мета тегтерін орнату
 * @param {Object} options - SEO параметрлері
 * @param {string} options.title - Беттің атауы
 * @param {string} options.description - Беттің сипаттамасы
 * @param {string} options.keywords - Кілт сөздер
 * @param {string} options.canonical - Canonical URL
 * @param {string} options.ogImage - Open Graph суреті
 * @param {string} options.ogType - Open Graph түрі (website, article, product)
 * @param {Object} options.structuredData - Structured data объектісі
 */
export function setSEO(options = {}) {
  const {
    title,
    description,
    keywords,
    canonical,
    ogImage = '/og-image.jpg',
    ogType = 'website',
    structuredData,
  } = options;

  const baseUrl = 'https://agroland.kz';
  const currentPath = window.location.pathname;
  const fullUrl = `${baseUrl}${currentPath}`;

  // Беттің тақырыбы
  setTitle(title);

  // Primary Meta Tags
  setMetaTag('description', description || 'Agroland.kz — Қазақстандағы ауыл шаруашылығы тауарларын сатып алу және сатуға арналған онлайн нарық');
  setMetaTag('keywords', keywords || 'ауыл шаруашылығы, агро, техника, құрал-жабдықтар, тұқым, тыңайтқыш, Қазақстан');
  setMetaTag('robots', 'index, follow');
  setMetaTag('author', 'Agroland');

  // Canonical URL
  setLinkTag('canonical', canonical || fullUrl);

  // Open Graph
  setMetaTag('og:type', ogType, 'property');
  setMetaTag('og:url', fullUrl, 'property');
  setMetaTag('og:title', title || 'Agroland.kz', 'property');
  setMetaTag('og:description', description || 'Ауыл шаруашылығы тауарларының нарығы', 'property');
  setMetaTag('og:image', `${baseUrl}${ogImage}`, 'property');
  setMetaTag('og:locale', 'kk_KZ', 'property');

  // Twitter
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:url', fullUrl, 'property');
  setMetaTag('twitter:title', title || 'Agroland.kz', 'property');
  setMetaTag('twitter:description', description || 'Ауыл шаруашылығы тауарларының нарығы', 'property');
  setMetaTag('twitter:image', `${baseUrl}${ogImage}`, 'property');

  // Structured Data
  if (structuredData) {
    setStructuredData(structuredData);
  }
}

/**
 * Product Schema.org деректерін құру
 * @param {Object} product - Өнім деректері
 * @returns {Object} Structured data объектісі
 */
export function createProductSchema(product) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images || [],
    "offers": {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": product.currency || "KZT",
      "availability": product.inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": product.sellerName || "Agroland.kz"
      }
    },
    "aggregateRating": product.rating && {
      "@type": "AggregateRating",
      "ratingValue": product.rating.value,
      "reviewCount": product.rating.count
    }
  };
}

/**
 * Article Schema.org деректерін құру (жаңалықтар үшін)
 * @param {Object} article - Мақала деректері
 * @returns {Object} Structured data объектісі
 */
export function createArticleSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image,
    "datePublished": article.datePublished,
    "dateModified": article.dateModified,
    "author": {
      "@type": "Organization",
      "name": "Agroland.kz"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Agroland.kz",
      "logo": {
        "@type": "ImageObject",
        "url": "https://agroland.kz/logo.png"
      }
    }
  };
}

/**
 * BreadcrumbList Schema.org деректерін құру
 * @param {Array} items - Breadcrumb элементтері
 * @returns {Object} Structured data объектісі
 */
export function createBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}

export default {
  setTitle,
  setSEO,
  setStructuredData,
  createProductSchema,
  createArticleSchema,
  createBreadcrumbSchema
};
